import { useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

/** Super-admin-only store sales reset with a type-to-confirm dialog. */
export function ResetSalesButton({ storeId, scopeLabel, onDone, user }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  if (user?.role !== "super_admin" || !storeId) return null;

  const PHRASE = "RESET SALES";
  const run = async () => {
    if (typed !== PHRASE) return;
    setBusy(true);
    try {
      const body = { confirm: PHRASE, store_id: storeId };
      const { data } = await api.post("/admin/reset-sales", body);
      toast.success(`Reset complete — ${data.orders_deleted} orders cleared`);
      setOpen(false); setTyped("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-3 h-8 rounded-full text-xs font-medium border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
        data-testid="reset-sales-trigger"
      >
        <Trash2 className="w-3.5 h-3.5" />
        Reset Sales
      </button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTyped(""); }}>
        <DialogContent data-testid="reset-sales-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" /> Reset sales data</DialogTitle>
            <DialogDescription>
              This permanently deletes all orders and payment attempts for <strong>{scopeLabel}</strong>. The sales dashboard will start from $0. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-neutral-700">Type <code className="px-1.5 py-0.5 bg-neutral-100 rounded text-xs">{PHRASE}</code> to confirm:</label>
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={PHRASE} data-testid="reset-sales-confirm-input" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="reset-sales-cancel">Cancel</Button>
            <Button variant="destructive" onClick={run} disabled={typed !== PHRASE || busy} data-testid="reset-sales-confirm">
              {busy ? "Resetting…" : "Delete all sales data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
